import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Pencil, Upload, Eye, X, FileText, FileSpreadsheet, FileDown, RefreshCw, Receipt, Wallet, Percent, ScrollText, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

const SUBTABS = [
  ["reports", "Reports", Wallet],
  ["expenditure", "Expenditure", Receipt],
  ["categories", "Categories", Layers],
  ["transactions", "Manual Transactions", FileText],
  ["tax", "Tax", Percent],
  ["audit", "Audit Log", ScrollText],
];

function Field({ label, children }) {
  return <label className="block text-xs text-stone-500">{label}<div className="mt-1">{children}</div></label>;
}
const inp = "w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30";

// ---------------- Reports ----------------
function Reports() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [filters, setFilters] = useState({ source: "", category: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dl, setDl] = useState("");
  const qs = () => {
    const p = [];
    if (range.start) p.push(`start=${range.start}`);
    if (range.end) p.push(`end=${range.end}`);
    if (filters.source) p.push(`source=${encodeURIComponent(filters.source)}`);
    if (filters.category) p.push(`category=${encodeURIComponent(filters.category)}`);
    return p.join("&");
  };
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/finance/report${qs() ? `?${qs()}` : ""}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [range, filters]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    setDl(fmt);
    try {
      const r = await api.get(`/admin/finance/report.${fmt}${qs() ? `?${qs()}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `rose-air-financial-report.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Could not export report"); }
    finally { setDl(""); }
  };
  const preset = (k) => {
    const d = new Date(); const y = d.getFullYear(); const m = d.getMonth();
    const iso = (dt) => dt.toISOString().slice(0, 10);
    if (k === "this-month") setRange({ start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) });
    else if (k === "last-month") setRange({ start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) });
    else if (k === "this-year") setRange({ start: `${y}-01-01`, end: `${y}-12-31` });
    else setRange({ start: "", end: "" });
  };

  const cards = data ? [
    ["Gross revenue", data.gross_revenue, "text-forest"],
    ["Less expenses", data.expenses, "text-amber-600"],
    ["Net revenue", data.net_revenue, "text-forest"],
    [`Estimated tax due (${data.tax_rate}%)`, data.estimated_tax_due, "text-rose"],
    ["Net after tax", data.net_after_tax, "text-green-700"],
  ] : [];

  return (
    <div data-testid="finance-reports">
      <div className="card-lux p-5 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1">
            {[["this-month", "This month"], ["last-month", "Last month"], ["this-year", "This year"], ["all", "All time"]].map(([k, l]) => (
              <button key={k} onClick={() => preset(k)} data-testid={`fin-preset-${k}`} className="text-xs px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-sand">{l}</button>
            ))}
          </div>
          <Field label="From"><input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="fin-start" className={inp} /></Field>
          <Field label="To"><input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="fin-end" className={inp} /></Field>
          <Field label="Source"><input placeholder="e.g. airbnb" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} data-testid="fin-source" className={inp} /></Field>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => download("csv")} disabled={!!dl} data-testid="fin-export-csv" className="btn-dark text-xs"><FileText size={14} /> CSV</button>
            <button onClick={() => download("xlsx")} disabled={!!dl} data-testid="fin-export-xlsx" className="btn-dark text-xs"><FileSpreadsheet size={14} /> XLSX</button>
            <button onClick={() => download("pdf")} disabled={!!dl} data-testid="fin-export-pdf" className="btn-primary text-xs"><FileDown size={14} /> PDF</button>
          </div>
        </div>
      </div>

      {loading || !data ? <Loader2 className="animate-spin text-rose mx-auto my-8" /> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="finance-summary-cards">
            {cards.map(([label, val, cls]) => (
              <div key={label} className="card-lux p-4">
                <div className="text-xs text-stone-500">{label}</div>
                <div className={`font-serif text-2xl mt-1 ${cls}`}>{money(val)}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="card-lux p-5">
              <h3 className="font-serif text-lg text-forest mb-3">Revenue by source</h3>
              {Object.keys(data.by_source).length === 0 ? <p className="text-sm text-stone-400">No revenue in this period.</p> :
                Object.entries(data.by_source).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm py-1.5 border-b border-stone-100 last:border-0"><span className="capitalize">{k}</span><span className="font-medium">{money(v)}</span></div>
                ))}
            </div>
            <div className="card-lux p-5">
              <h3 className="font-serif text-lg text-forest mb-3">Expenses by category</h3>
              {Object.keys(data.by_category).length === 0 ? <p className="text-sm text-stone-400">No expenses in this period.</p> :
                Object.entries(data.by_category).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm py-1.5 border-b border-stone-100 last:border-0"><span>{k}</span><span className="font-medium">{money(v)}</span></div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Categories ----------------
function Categories() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/admin/finance/categories").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/admin/finance/categories", { name }); setName(""); toast.success("Category added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { await api.patch(`/admin/finance/categories/${c.id}`, { archived: !c.archived }); load(); };
  const rename = async (c) => { const n = window.prompt("Rename category", c.name); if (n && n.trim()) { await api.patch(`/admin/finance/categories/${c.id}`, { name: n.trim() }); load(); } };
  const del = async (c) => { if (window.confirm(`Delete category "${c.name}"?`)) { await api.delete(`/admin/finance/categories/${c.id}`); load(); } };
  return (
    <div data-testid="finance-categories">
      <div className="card-lux p-5 mb-4 flex items-end gap-2">
        <Field label="New category"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name" className={inp} placeholder="e.g. Landscaping" /></Field>
        <button onClick={add} data-testid="cat-add" className="btn-primary text-sm"><Plus size={15} /> Add</button>
      </div>
      <div className="card-lux overflow-hidden">
        <table className="w-full text-sm" data-testid="categories-table">
          <thead className="bg-sand/60 text-stone-500 text-xs uppercase"><tr><th className="text-left p-4">Category</th><th className="text-left p-4">Status</th><th className="text-left p-4">Actions</th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-stone-100">
                <td className="p-4 font-medium text-forest">{c.name}</td>
                <td className="p-4">{c.archived ? <span className="text-xs text-stone-400">Archived</span> : <span className="text-xs text-green-700">Active</span>}</td>
                <td className="p-4"><div className="flex gap-2">
                  <button onClick={() => rename(c)} className="p-1.5 rounded-md bg-stone-100 hover:bg-stone-200"><Pencil size={14} /></button>
                  <button onClick={() => toggle(c)} className="text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700">{c.archived ? "Unarchive" : "Archive"}</button>
                  <button onClick={() => del(c)} className="p-1.5 rounded-md bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-stone-400">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Expenditure ----------------
const EMPTY_EXP = { date: today(), category: "", amount: "", vat_amount: "", supplier: "", description: "" };
function Expenditure() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY_EXP);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ category: "", q: "" });
  const load = useCallback(() => {
    const p = [];
    if (filter.category) p.push(`category=${encodeURIComponent(filter.category)}`);
    if (filter.q) p.push(`q=${encodeURIComponent(filter.q)}`);
    api.get(`/admin/finance/expenses${p.length ? `?${p.join("&")}` : ""}`).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/finance/categories").then((r) => setCats(r.data.filter((c) => !c.archived))).catch(() => {}); }, []);

  const save = async () => {
    if (!form.date || !form.amount) { toast.error("Date and amount are required"); return; }
    const body = { ...form, amount: Number(form.amount), vat_amount: Number(form.vat_amount || 0) };
    try {
      if (editing) await api.patch(`/admin/finance/expenses/${editing}`, body);
      else await api.post("/admin/finance/expenses", body);
      toast.success(editing ? "Expense updated" : "Expense recorded");
      setForm(EMPTY_EXP); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (e) => { setEditing(e.id); setForm({ date: e.date, category: e.category, amount: e.amount, vat_amount: e.vat_amount, supplier: e.supplier, description: e.description }); };
  const del = async (id) => { if (window.confirm("Delete this expense?")) { await api.delete(`/admin/finance/expenses/${id}`); load(); } };
  const uploadReceipt = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/finance/expenses/${id}/receipt`, fd); toast.success("Receipt attached"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const viewReceipt = async (id) => {
    try { const r = await api.get(`/admin/finance/expenses/${id}/receipt`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch (e) { toast.error("Could not open receipt"); }
  };
  const delReceipt = async (id) => { if (window.confirm("Remove receipt?")) { await api.delete(`/admin/finance/expenses/${id}/receipt`); load(); } };

  return (
    <div data-testid="finance-expenditure">
      <div className="card-lux p-5 mb-4">
        <h3 className="font-serif text-lg text-forest mb-3">{editing ? "Edit expense" : "Record an expense"}</h3>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Date"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" className={inp} /></Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="exp-category" className={inp}>
              <option value="">—</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Amount"><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" className={inp} /></Field>
          <Field label="VAT amount"><input type="number" step="0.01" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} data-testid="exp-vat" className={inp} /></Field>
          <Field label="Supplier"><input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="exp-supplier" className={inp} /></Field>
          <Field label="Description"><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="exp-description" className={inp} /></Field>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="exp-save" className="btn-primary text-sm">{editing ? "Update expense" : "Add expense"}</button>
          {editing && <button onClick={() => { setEditing(null); setForm(EMPTY_EXP); }} className="text-sm px-4 py-2 rounded-full border border-stone-200">Cancel</button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} data-testid="exp-filter-category" className={`${inp} max-w-48`}>
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <input placeholder="Search supplier / description" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="exp-search" className={`${inp} max-w-64`} />
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="bg-sand/60 text-stone-500 text-xs uppercase"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Category</th><th className="text-left p-3">Supplier</th><th className="text-left p-3">Description</th><th className="text-left p-3">Amount</th><th className="text-left p-3">VAT</th><th className="text-left p-3">Receipt</th><th className="text-left p-3">Actions</th></tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-stone-100" data-testid={`expense-row-${e.id}`}>
                  <td className="p-3 whitespace-nowrap">{e.date}</td>
                  <td className="p-3">{e.category || "—"}</td>
                  <td className="p-3">{e.supplier || "—"}</td>
                  <td className="p-3 max-w-40 truncate">{e.description || "—"}</td>
                  <td className="p-3 font-semibold">{money(e.amount)}</td>
                  <td className="p-3 text-stone-500">{money(e.vat_amount)}</td>
                  <td className="p-3">
                    {e.receipt ? (
                      <div className="flex gap-1">
                        <button onClick={() => viewReceipt(e.id)} data-testid={`receipt-view-${e.id}`} title="View" className="p-1.5 rounded bg-stone-100"><Eye size={13} /></button>
                        <button onClick={() => delReceipt(e.id)} title="Remove" className="p-1.5 rounded bg-red-50 text-red-600"><X size={13} /></button>
                      </div>
                    ) : (
                      <label className="cursor-pointer text-xs text-rose font-semibold inline-flex items-center gap-1" data-testid={`receipt-upload-${e.id}`}><Upload size={13} /> Attach
                        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(ev) => uploadReceipt(e.id, ev.target.files?.[0])} /></label>
                    )}
                  </td>
                  <td className="p-3"><div className="flex gap-2">
                    <button onClick={() => edit(e)} className="p-1.5 rounded-md bg-stone-100 hover:bg-stone-200"><Pencil size={13} /></button>
                    <button onClick={() => del(e.id)} className="p-1.5 rounded-md bg-red-50 text-red-600"><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-stone-400">No expenses recorded.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- Manual transactions ----------------
const EMPTY_TXN = { date: today(), type: "income", amount: "", description: "", note: "" };
function Transactions() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_TXN);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/finance/transactions").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.date || form.amount === "") { toast.error("Date and amount are required"); return; }
    const body = { ...form, amount: Number(form.amount) };
    try {
      if (editing) await api.patch(`/admin/finance/transactions/${editing}`, body);
      else await api.post("/admin/finance/transactions", body);
      toast.success(editing ? "Transaction updated" : "Transaction recorded");
      setForm(EMPTY_TXN); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (t) => { setEditing(t.id); setForm({ date: t.date, type: t.type, amount: t.amount, description: t.description, note: t.note }); };
  const del = async (id) => { if (window.confirm("Delete this transaction?")) { await api.delete(`/admin/finance/transactions/${id}`); load(); } };
  return (
    <div data-testid="finance-transactions">
      <div className="card-lux p-5 mb-4">
        <h3 className="font-serif text-lg text-forest mb-3">{editing ? "Edit transaction" : "Record a manual transaction"}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Date"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="txn-date" className={inp} /></Field>
          <Field label="Type"><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="txn-type" className={inp}><option value="income">Income</option><option value="adjustment">Adjustment</option></select></Field>
          <Field label="Amount"><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="txn-amount" className={inp} /></Field>
          <Field label="Description"><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="txn-description" className={inp} /></Field>
          <Field label="Note"><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="txn-note" className={inp} /></Field>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="txn-save" className="btn-primary text-sm">{editing ? "Update" : "Add transaction"}</button>
          {editing && <button onClick={() => { setEditing(null); setForm(EMPTY_TXN); }} className="text-sm px-4 py-2 rounded-full border border-stone-200">Cancel</button>}
        </div>
      </div>
      <div className="card-lux overflow-hidden">
        <table className="w-full text-sm" data-testid="transactions-table">
          <thead className="bg-sand/60 text-stone-500 text-xs uppercase"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Description</th><th className="text-left p-3">Note</th><th className="text-left p-3">Actions</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-stone-100" data-testid={`txn-row-${t.id}`}>
                <td className="p-3 whitespace-nowrap">{t.date}</td>
                <td className="p-3 capitalize">{t.type}</td>
                <td className="p-3 font-semibold">{money(t.amount)}</td>
                <td className="p-3">{t.description || "—"}</td>
                <td className="p-3 text-stone-500">{t.note || "—"}</td>
                <td className="p-3"><div className="flex gap-2">
                  <button onClick={() => edit(t)} className="p-1.5 rounded-md bg-stone-100 hover:bg-stone-200"><Pencil size={13} /></button>
                  <button onClick={() => del(t.id)} className="p-1.5 rounded-md bg-red-50 text-red-600"><Trash2 size={13} /></button>
                </div></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-400">No manual transactions.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Tax ----------------
function Tax() {
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(0);
  const [form, setForm] = useState({ rate: "", effective_from: today() });
  const load = useCallback(() => api.get("/admin/finance/tax-rates").then((r) => { setRows(r.data.rates); setCurrent(r.data.current); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (form.rate === "") { toast.error("Enter a rate"); return; }
    try { await api.post("/admin/finance/tax-rates", { rate: Number(form.rate), effective_from: form.effective_from }); toast.success("Tax rate saved"); setForm({ rate: "", effective_from: today() }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete this rate?")) { await api.delete(`/admin/finance/tax-rates/${id}`); load(); } };
  return (
    <div data-testid="finance-tax">
      <div className="card-lux p-5 mb-4">
        <div className="text-sm text-stone-500">Current effective tax rate</div>
        <div className="font-serif text-3xl text-rose" data-testid="tax-current">{current}%</div>
        <p className="text-xs text-stone-400 mt-1">Estimated tax = Net revenue (income − expenses) × rate effective for the period.</p>
      </div>
      <div className="card-lux p-5 mb-4 flex items-end gap-3">
        <Field label="New rate (%)"><input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="tax-rate-input" className={inp} /></Field>
        <Field label="Effective from"><input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="tax-effective-from" className={inp} /></Field>
        <button onClick={add} data-testid="tax-add" className="btn-primary text-sm"><Plus size={15} /> Save rate</button>
      </div>
      <div className="card-lux overflow-hidden">
        <table className="w-full text-sm" data-testid="tax-rates-table">
          <thead className="bg-sand/60 text-stone-500 text-xs uppercase"><tr><th className="text-left p-3">Effective from</th><th className="text-left p-3">Rate</th><th className="text-left p-3">Set by</th><th className="text-left p-3">Actions</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-stone-100">
                <td className="p-3">{r.effective_from}</td>
                <td className="p-3 font-semibold">{r.rate}%</td>
                <td className="p-3 text-stone-500 text-xs">{r.created_by}</td>
                <td className="p-3"><button onClick={() => del(r.id)} className="p-1.5 rounded-md bg-red-50 text-red-600"><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-stone-400">No historical rates yet — the current rate falls back to the statement tax setting.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Audit ----------------
function Audit() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/finance/audit${entity ? `?entity_type=${entity}` : ""}`).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [entity]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="finance-audit">
      <div className="flex items-center gap-2 mb-3">
        <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="audit-entity-filter" className={`${inp} max-w-56`}>
          <option value="">All finance actions</option>
          <option value="expense">Expenses</option>
          <option value="manual_transaction">Revenue / transactions</option>
          <option value="tax_rate">Tax settings</option>
          <option value="expense_category">Categories</option>
          <option value="identity">Identity verification</option>
          <option value="payment_provider">Payment gateways</option>
          <option value="ical_feed">Calendar feeds</option>
        </select>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-full bg-forest text-cream inline-flex items-center gap-1"><RefreshCw size={13} /> Refresh</button>
      </div>
      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="audit-table">
            <thead className="bg-sand/60 text-stone-500 uppercase"><tr><th className="text-left p-3">When (UTC)</th><th className="text-left p-3">User</th><th className="text-left p-3">Action</th><th className="text-left p-3">Entity</th><th className="text-left p-3">Change</th><th className="text-left p-3">IP</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center"><Loader2 className="animate-spin text-rose mx-auto" /></td></tr> :
                rows.map((l) => (
                  <tr key={l.id} className="border-t border-stone-100">
                    <td className="p-3 whitespace-nowrap">{(l.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="p-3">{l.user}</td>
                    <td className="p-3 font-medium text-forest">{l.action}</td>
                    <td className="p-3">{l.entity_type}</td>
                    <td className="p-3 text-stone-500">{l.new_value ? JSON.stringify(l.new_value) : l.old_value ? JSON.stringify(l.old_value) : "—"}</td>
                    <td className="p-3 text-stone-400">{l.ip || "—"}</td>
                  </tr>
                ))}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-400">No audit entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Finance() {
  const [sub, setSub] = useState("reports");
  return (
    <div data-testid="finance-module">
      <div className="flex flex-wrap gap-2 mb-5 border-b border-stone-200 pb-3" data-testid="finance-subtabs">
        {SUBTABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`finance-subtab-${id}`}
            className={`text-sm px-4 py-2 rounded-full font-medium inline-flex items-center gap-2 transition-colors ${sub === id ? "bg-rose text-white" : "bg-sand text-forest hover:bg-rose/10"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {sub === "reports" && <Reports />}
      {sub === "expenditure" && <Expenditure />}
      {sub === "categories" && <Categories />}
      {sub === "transactions" && <Transactions />}
      {sub === "tax" && <Tax />}
      {sub === "audit" && <Audit />}
    </div>
  );
}
